import TnButton from '@components/tn-button/TnButton';
import TnTypography from '@components/tn-typography/TnTypography';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import axios from 'axios';
import React, { useRef, useState } from 'react';

import { ReactComponent as FileUpload } from '../../static/_assets/file-upload.svg';

interface Props {
  documentUrl?: string;
  documentName?: string;
  onUpload: (fileName: string, url: string) => void;
  onDelete: () => void;
  onUploadError?: () => void;
}

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.txt';

const uploadConfig = {
  headers: {
    'Access-Control-Allow-Origin': '*',
  },
};

const useStyles = makeStyles(() =>
  createStyles({
    labelContainer: {
      marginBottom: 8,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 4,
    },
    uploadBox: {
      width: '100%',
      padding: '16px',
      border: '1px solid #E0E0E0',
      borderRadius: 4,
      backgroundColor: '#FAFAFA',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 8,
      boxSizing: 'border-box',
    },
    uploadLabelContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 8,
    },
    uploadedBox: {
      width: '100%',
      padding: '16px',
      border: '1px solid #E0E0E0',
      borderRadius: 4,
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxSizing: 'border-box',
    },
    deleteIconContainer: {
      cursor: 'pointer',
      border: '1px solid rgba(176, 0, 32, 0.5)',
      padding: '4px 4px 0px 4px',
      borderRadius: 4,
    },
    deleteIcon: {
      fill: '#B00020',
    },
    hiddenInput: {
      display: 'none',
    },
  }),
);

export default function JobDescriptionUploader({
  documentUrl,
  documentName,
  onUpload,
  onDelete,
  onUploadError,
}: Readonly<Props>) {
  const classes = useStyles();
  const inputRef: React.MutableRefObject<HTMLInputElement | undefined> = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFileChange = () => {
    if (!inputRef.current?.files || inputRef.current.files.length === 0) return;

    const files = inputRef.current.files;
    const data = new FormData();
    data.append('file', files[0]);
    data.append('container_name', 'job');
    data.append('nested_container_name', '');

    setUploading(true);

    axios
      .post(`${process.env.UPLOAD_URI}/upload-file`, data, uploadConfig)
      .then((response) => {
        const fileUrl = response.data.result ? response.data.result.urls[1] : response.data;
        onUpload(files[0].name, fileUrl);
        setUploading(false);
      })
      .catch(() => {
        setUploading(false);
        onUploadError?.();
      });
  };

  const handleDelete = () => {
    if (documentUrl) {
      const blobName = documentUrl.split('?')[0].split('/').pop();
      axios
        .post(`${process.env.UPLOAD_URI}/delete-file`, {
          container_name: 'job',
          file_name: blobName,
        })
        .then(() => {
          onDelete();
        })
        .catch(() => {
          onDelete();
        });
    } else {
      onDelete();
    }
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  return (
    <Grid container>
      <Grid item xs={12} className={classes.labelContainer}>
        <div className={classes.titleRow}>
          <TnTypography bold variant="body2">
            Job Description
          </TnTypography>
          <TnTypography variant="body2" component="span">
            (Optional)
          </TnTypography>
        </div>
        <TnTypography variant="caption2" color="lightGrey">
          Upload a file that details Candidate Profile, Job Description or Responsibilities.
        </TnTypography>
      </Grid>

      <Grid item xs={12}>
        {documentUrl ? (
          <div className={classes.uploadedBox}>
            <TnTypography variant="caption2">{documentName || 'Job Description'}</TnTypography>
            <div aria-hidden="true" className={classes.deleteIconContainer} onClick={handleDelete}>
              <DeleteOutline className={classes.deleteIcon} />
            </div>
          </div>
        ) : (
          <div className={classes.uploadBox}>
            <FileUpload />
            <div className={classes.uploadLabelContainer}>
              <TnTypography bold variant="body2">
                Upload the Job Description
              </TnTypography>
            </div>
            <TnTypography variant="caption2" color="lightGrey">
              Maximum 3MB. Supports .pdf, .doc, .docx, .txt
            </TnTypography>
            <TnButton
              value={uploading ? 'Uploading...' : 'Upload'}
              color="primary"
              variant="contained"
              onClick={handleUploadClick}
            />
            <input
              type="file"
              className={classes.hiddenInput}
              ref={inputRef as React.RefObject<HTMLInputElement>}
              onChange={handleFileChange}
              accept={ACCEPTED_TYPES}
            />
          </div>
        )}
      </Grid>
    </Grid>
  );
}
